/**
 * MockLevelService — a faithful, localStorage-backed stand-in for the sharing
 * backend (Brief 008). Lets the Publish/Browse UI be built and tested with ZERO
 * infrastructure, then swapped for the real HTTP backend by flipping env.
 *
 * "Faithful" = it enforces the same contract as the server: it runs the shared
 * `prepareForPublish` (validate + stamp + size cap), it's idempotent on content
 * (re-publishing the same level returns the same record, per docs/SHARING_API.md),
 * and `get` on an unknown id throws SharingError('not-found'). It is NOT a
 * happy-path fake — code that works against the mock works against the server.
 *
 * Persistence is best-effort: the in-memory Map is the source of truth, mirrored
 * to localStorage when present (browser) and skipped when absent (node test env).
 */
import { type LevelFormat } from '../engine/LevelFormat';
import { mutatorSetId } from '../engine/Mutators';
import { sanitizeArchetype } from '../engine/Archetype';
import type { Replay } from '../replay/Replay';
import { prepareForPublish, SharingError, type LevelService } from './LevelService';
import { LIMITS } from './config';
import { forgetOwnerToken, getOwnerToken, rememberOwnerToken } from './ownerTokens';
import type {
  DailyResponse, DeathPoint, ForksResponse, HeatmapResponse, LevelListResponse, LevelSummary,
  ListParams, PublishResponse, PublishedLevel, RunDetail, RunListParams, RunListResponse,
  RunSummary, SubmitRunResponse,
} from './types';

const STORE_KEY = 'maker.sharing.mock.v1';
const RUNS_KEY = 'maker.sharing.mock.runs.v1';
const DEATHS_KEY = 'maker.sharing.mock.deaths.v1';
const DEFAULT_PAGE_SIZE = 24;
const MAX_PAGE_SIZE = 100;

/** Internal row: the wire record plus a monotonic insertion seq for stable sort. */
interface MockRow { rec: PublishedLevel; seq: number; }

export class MockLevelService implements LevelService {
  private rows = new Map<string, MockRow>();
  private seq = 0;
  /** Leaderboard runs (Tier 1, Phase B), keyed by run id; carries the replay payload. */
  private runs = new Map<string, RunDetail>();
  private runSeq = 0;
  /** Death heatmap (Tier 2, Feature F): content levelId → ("x,y" → count). Keyed on the
   *  CONTENT hash (like runs) so it follows a level's geometry across re-uploads. */
  private deaths = new Map<string, Map<string, number>>();

  constructor() { this.hydrate(); this.hydrateRuns(); this.hydrateDeaths(); }

  /** Owner tokens (POLISH_04 #1), kept in-memory + persisted alongside the rows so the
   *  mock enforces the SAME unpublish contract as the server: a raw token issued once
   *  at publish, checked (not stored in the clear anywhere the UI can read it back)
   *  on DELETE. Keyed by pub_ id. */
  private ownerTokens = new Map<string, string>();

  async publish(level: LevelFormat, opts?: { cleared?: boolean; thumbnail?: string; clearRun?: Replay }): Promise<PublishResponse> {
    const { level: prepared, levelId } = prepareForPublish(level);

    // Idempotent: same CONTENT (levelId) maps to the same record — re-publishing an
    // unchanged level is a no-op return, not a duplicate. (cleared flag is
    // server-trusted; the mock accepts it unconditionally.) No fresh ownerToken on an
    // idempotent re-publish — mirrors the server (ownerToken is publish-once).
    for (const { rec } of this.rows.values()) {
      if (rec.levelId === levelId) return { id: rec.id, slug: rec.slug, levelId };
    }

    const seq = ++this.seq;
    const id = `pub_${seq.toString(36)}_${levelId.slice(0, 4)}`;
    const name = clamp(prepared.meta.name || 'Untitled', LIMITS.maxNameLen);
    // POLISH_04 #2 — mock has no re-sim engine; a syntactically-present clearRun
    // (levelId matches + non-empty frames) is trusted as verified, which is the
    // faithful mock-equivalent of "the server proved it" for local dev/testing.
    const verifiedClear = !!opts?.clearRun && opts.clearRun.levelId === levelId
      && Array.isArray(opts.clearRun.frames) && opts.clearRun.frames.length > 0;
    const rec: PublishedLevel = {
      id,
      slug: slugify(name) || 'level',
      name,
      author: clamp(prepared.meta.author || 'anon', LIMITS.maxAuthorLen),
      levelId,
      formProfile: prepared.meta.formProfile,
      theme: prepared.theme,
      plays: 0,
      publishedAt: new Date().toISOString(),
      // Mock has no thumb endpoint — stash the data URL directly as thumbUrl (<img> eats both).
      hasThumb: !!opts?.thumbnail,
      thumbUrl: opts?.thumbnail,
      // Tier 2: lineage pointer (Feature E) + clears counter (Feature F).
      parentLevelId: prepared.meta.parentLevelId,
      clears: 0,
      verifiedClear,
      level: prepared,
    };
    this.rows.set(id, { rec, seq });
    const ownerToken = `tok_${id}_${Math.random().toString(36).slice(2)}`;
    this.ownerTokens.set(id, ownerToken);
    this.persist();
    rememberOwnerToken(id, ownerToken); // mirror HttpLevelService: transport persists it
    return { id, slug: rec.slug, levelId, ownerToken };
  }

  // ── UGC lifecycle (POLISH_04 #1) — mirrors the server's unpublish/report contract ──

  async unpublish(levelPubId: string): Promise<void> {
    const token = getOwnerToken(levelPubId);
    if (!token || this.ownerTokens.get(levelPubId) !== token) {
      throw new SharingError('invalid', 'no owner token for this level (not published from this browser?)');
    }
    this.rows.delete(levelPubId);
    this.ownerTokens.delete(levelPubId);
    forgetOwnerToken(levelPubId);
    this.persist();
  }

  async report(levelPubId: string, _reason?: string): Promise<void> {
    void _reason; // mock doesn't queue reports anywhere readable; just validates the target exists
    if (!this.rows.has(levelPubId)) throw new SharingError('not-found', `no published level with id ${levelPubId}`);
  }

  async list(params?: ListParams): Promise<LevelListResponse> {
    const sort = params?.sort ?? 'new';
    const page = Math.max(1, params?.page ?? 1);
    const pageSize = Math.min(MAX_PAGE_SIZE, Math.max(1, params?.pageSize ?? DEFAULT_PAGE_SIZE));
    const q = (params?.q ?? '').trim().toLowerCase();

    let rows = [...this.rows.values()];
    if (q) {
      rows = rows.filter(({ rec }) =>
        rec.name.toLowerCase().includes(q) || rec.author.toLowerCase().includes(q));
    }
    rows.sort((a, b) =>
      sort === 'plays' ? (b.rec.plays - a.rec.plays) || (b.seq - a.seq) : (b.seq - a.seq));

    const total = rows.length;
    const start = (page - 1) * pageSize;
    const items: LevelSummary[] = rows.slice(start, start + pageSize).map(({ rec }) => this.summaryOf(rec));
    return { items, page, pageSize, total };
  }

  async get(id: string): Promise<PublishedLevel> {
    const row = this.rows.get(id);
    if (!row) throw new SharingError('not-found', `no published level with id ${id}`);
    return this.withRemix(structuredClone(row.rec));
  }

  async recordPlay(id: string): Promise<void> {
    const row = this.rows.get(id);
    if (!row) return; // best-effort, mirror the http transport's swallow
    row.rec.plays += 1;
    this.persist();
  }

  // ── Leaderboards (Tier 1, Phase B) — same rejects as the server ──────────────

  async submitRun(levelPubId: string, replay: Replay, author: string): Promise<SubmitRunResponse> {
    const row = this.rows.get(levelPubId);
    if (!row) throw new SharingError('not-found', `no published level with id ${levelPubId}`);
    if (replay.levelId !== row.rec.levelId) {
      throw new SharingError('invalid', 'replay levelId does not match the target level');
    }
    if (!Number.isInteger(replay.clearFrame) || replay.clearFrame <= 0) {
      throw new SharingError('invalid', 'replay has no valid clear time');
    }
    const id = `run_${(++this.runSeq).toString(36)}_${replay.levelId.slice(0, 4)}`;
    // Tier 3 (Feature H) + archetype: derive the category from the replay's mutator set AND
    // archetype (recompute, never trust a client number) so the mock mirrors the server's
    // board-keying exactly.
    const setId = mutatorSetId(replay.mutators ?? []);
    const arche = sanitizeArchetype(replay.archetype);
    const detail: RunDetail = {
      id,
      levelId: replay.levelId,
      author: clamp(author || 'anon', LIMITS.maxAuthorLen),
      timeFrames: replay.clearFrame,
      verified: false, // mock mirrors Phase B (no re-sim); Phase C server flips this
      createdAt: new Date().toISOString(),
      mutatorSetId: setId,
      archetype: arche,
      replay: structuredClone(replay),
    };
    this.runs.set(id, detail);
    this.persistRuns();
    return { id, timeFrames: detail.timeFrames, verified: detail.verified, mutatorSetId: setId, archetype: arche };
  }

  async getRuns(levelPubId: string, params?: RunListParams): Promise<RunListResponse> {
    const row = this.rows.get(levelPubId);
    if (!row) throw new SharingError('not-found', `no published level with id ${levelPubId}`);
    const levelId = row.rec.levelId;
    const limit = Math.min(100, Math.max(1, params?.limit ?? 20));
    const setId = params?.mutatorSetId ?? 0; // default board = vanilla
    const arche = sanitizeArchetype(params?.archetype); // default board = classic
    const items: RunSummary[] = [...this.runs.values()]
      .filter((r) => r.levelId === levelId && (r.mutatorSetId ?? 0) === setId
        && sanitizeArchetype(r.archetype) === arche)
      .sort((a, b) => (a.timeFrames - b.timeFrames) || a.createdAt.localeCompare(b.createdAt))
      .slice(0, limit)
      .map(toRunSummary);
    return { items };
  }

  async getReplay(runId: string): Promise<RunDetail> {
    const r = this.runs.get(runId);
    if (!r) throw new SharingError('not-found', `no run with id ${runId}`);
    return structuredClone(r);
  }

  async getDaily(): Promise<DailyResponse> {
    const date = new Date().toISOString().slice(0, 10);
    const rows = [...this.rows.values()].sort((a, b) => a.seq - b.seq); // stable publish order
    if (rows.length === 0) return { date, level: null };
    const pick = rows[dateHash(date) % rows.length].rec;
    return { date, level: this.withRemix(structuredClone(pick)) };
  }

  // ── Fork / lineage (Tier 2, Feature E) — same shape as the server ────────────

  async getForks(levelPubId: string): Promise<ForksResponse> {
    const row = this.rows.get(levelPubId);
    if (!row) throw new SharingError('not-found', `no published level with id ${levelPubId}`);
    const parentLevelId = row.rec.levelId;
    const items: LevelSummary[] = [...this.rows.values()]
      .filter(({ rec }) => rec.parentLevelId === parentLevelId)
      .sort((a, b) => b.seq - a.seq)
      .map(({ rec }) => this.summaryOf(rec));
    return { items };
  }

  /** Number of published levels whose parent is this content levelId (Feature E hub count). */
  private remixCountFor(levelId: string): number {
    let n = 0;
    for (const { rec } of this.rows.values()) if (rec.parentLevelId === levelId) n++;
    return n;
  }

  /** Stamp the (read-time) remixCount onto a summary/published record. */
  private summaryOf(rec: PublishedLevel): LevelSummary {
    return { ...toSummary(rec), remixCount: this.remixCountFor(rec.levelId) };
  }
  private withRemix(rec: PublishedLevel): PublishedLevel {
    rec.remixCount = this.remixCountFor(rec.levelId);
    return rec;
  }

  // ── Death heatmap + clear-rate (Tier 2, Feature F) — same shape as the server ──

  async submitDeaths(levelPubId: string, points: DeathPoint[]): Promise<void> {
    const row = this.rows.get(levelPubId);
    if (!row) return; // best-effort, mirror recordPlay's swallow
    const levelId = row.rec.levelId;
    const cells = this.deaths.get(levelId) ?? new Map<string, number>();
    for (const p of points.slice(0, LIMITS.maxDeathsPerSubmit)) {
      if (!Number.isInteger(p.x) || !Number.isInteger(p.y)) continue;
      const k = `${p.x},${p.y}`;
      cells.set(k, (cells.get(k) ?? 0) + 1);
    }
    this.deaths.set(levelId, cells);
    this.persistDeaths();
  }

  async recordClear(levelPubId: string): Promise<void> {
    const row = this.rows.get(levelPubId);
    if (!row) return; // best-effort
    row.rec.clears = (row.rec.clears ?? 0) + 1;
    this.persist();
  }

  async getHeatmap(levelPubId: string): Promise<HeatmapResponse> {
    const row = this.rows.get(levelPubId);
    if (!row) throw new SharingError('not-found', `no published level with id ${levelPubId}`);
    const cells = [...(this.deaths.get(row.rec.levelId) ?? new Map<string, number>()).entries()]
      .map(([k, n]) => { const [x, y] = k.split(',').map(Number); return { x, y, n }; });
    return { cells, plays: row.rec.plays, clears: row.rec.clears ?? 0 };
  }

  // ── persistence (best-effort) ─────────────────────────────────────────────
  private hydrate(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as MockRow[];
      for (const row of saved) {
        this.rows.set(row.rec.id, row);
        if (row.seq > this.seq) this.seq = row.seq;
      }
    } catch { /* corrupt / quota / private mode — start empty */ }
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try { localStorage.setItem(STORE_KEY, JSON.stringify([...this.rows.values()])); }
    catch { /* quota / private mode — non-fatal */ }
  }

  private hydrateRuns(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(RUNS_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as RunDetail[];
      for (const r of saved) this.runs.set(r.id, r);
      this.runSeq = saved.length;
    } catch { /* corrupt / quota / private mode — start empty */ }
  }

  private persistRuns(): void {
    if (typeof localStorage === 'undefined') return;
    try { localStorage.setItem(RUNS_KEY, JSON.stringify([...this.runs.values()])); }
    catch { /* quota / private mode — non-fatal */ }
  }

  private hydrateDeaths(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(DEATHS_KEY);
      if (!raw) return;
      // Stored as [levelId, [[cellKey, n], ...]][] (Map → entries arrays).
      const saved = JSON.parse(raw) as [string, [string, number][]][];
      for (const [levelId, cells] of saved) this.deaths.set(levelId, new Map(cells));
    } catch { /* corrupt / quota / private mode — start empty */ }
  }

  private persistDeaths(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const flat = [...this.deaths.entries()].map(([levelId, cells]) => [levelId, [...cells.entries()]]);
      localStorage.setItem(DEATHS_KEY, JSON.stringify(flat));
    } catch { /* quota / private mode — non-fatal */ }
  }
}

// ── helpers ─────────────────────────────────────────────────────────────────--

function toSummary(rec: PublishedLevel): LevelSummary {
  const { level: _level, ...summary } = rec; // strip the heavy payload for listings
  void _level;
  return summary;
}

function toRunSummary(rec: RunDetail): RunSummary {
  const { replay: _replay, ...summary } = rec; // strip the heavy replay for the board list
  void _replay;
  return summary;
}

function clamp(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Integer FNV-1a over the date string → stable per-day pick seed (mirrors the server). */
function dateHash(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}
